import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, Clock, CheckCircle, Truck, XCircle, Search, 
  Filter, Download, AlertCircle, DollarSign, MapPin, Calendar 
} from 'lucide-react';
import Header from '../../components/layout/Header';
import { useAuth } from '../../context/AuthContext';
import { getOrdersByBuyer } from '../../services/api';

const MyOrders = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchOrders();
  }, [user, navigate]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await getOrdersByBuyer(user.user_id);
      if (response.success) {
        setOrders(response.data || []);
      }
    } catch (error) {
      // console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  // Map API status to display status
  const getDisplayStatus = (order) => {
    if (order.disputed === 1) return 'disputed';
    if (order.status === 'cancelled' || order.status === 'rejected') return 'cancelled';
    if (order.actual_delivery_date || order.delivered_at) return 'delivered';
    if (order.shipped_at) return 'shipped';
    if (order.payment_status === 'paid' && order.status === 'accepted') return 'processing';
    if (order.payment_status === 'pending' && order.status === 'accepted') return 'pending_payment';
    if (order.payment_status === 'pending' && order.status === 'pending') return 'pending_payment';
    return order.status;
  };

  const getStatusIcon = (order) => {
    const status = getDisplayStatus(order);
    switch (status) {
      case 'pending_payment':
      case 'pending': return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'processing': 
      case 'accepted': return <Package className="w-5 h-5 text-blue-500" />;
      case 'shipped': return <Truck className="w-5 h-5 text-purple-500" />;
      case 'delivered': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'cancelled':
      case 'rejected': 
      case 'disputed': return <XCircle className="w-5 h-5 text-red-500" />;
      default: return <Package className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (order) => {
    const status = getDisplayStatus(order);
    switch (status) {
      case 'pending_payment':
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'processing':
      case 'accepted': return 'bg-blue-100 text-blue-800';
      case 'shipped': return 'bg-purple-100 text-purple-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'cancelled':
      case 'rejected':
      case 'disputed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (order) => {
    const status = getDisplayStatus(order);
    switch (status) {
      case 'pending_payment': return 'Pending Payment';
      case 'pending': return 'Pending';
      case 'processing': 
      case 'accepted': return 'Processing';
      case 'shipped': return 'Shipped';
      case 'delivered': return 'Delivered';
      case 'cancelled': return 'Cancelled';
      case 'rejected': return 'Rejected';
      case 'disputed': return 'Disputed';
      default: return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const filteredOrders = orders.filter(order => {
    const status = getDisplayStatus(order);
    
    if (filter !== 'all') {
      if (filter === 'pending' && !['pending', 'pending_payment'].includes(status)) return false;
      if (filter === 'processing' && !['processing', 'accepted'].includes(status)) return false;
      if (filter === 'shipped' && status !== 'shipped') return false;
      if (filter === 'delivered' && status !== 'delivered') return false;
      if (filter === 'cancelled' && !['cancelled', 'rejected', 'disputed'].includes(status)) return false;
    }
    
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      return (
        order.order_id.toString().includes(searchLower) ||
        (order.listing_title && order.listing_title.toLowerCase().includes(searchLower)) ||
        (order.category_name && order.category_name.toLowerCase().includes(searchLower)) ||
        (order.seller_first_name && `${order.seller_first_name} ${order.seller_last_name}`.toLowerCase().includes(searchLower))
      );
    }
    return true;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTotalOrders = () => {
    const stats = {
      all: orders.length,
      pending: 0,
      processing: 0,
      shipped: 0,
      delivered: 0,
      cancelled: 0,
    };

    orders.forEach(order => {
      const status = getDisplayStatus(order);
      switch (status) {
        case 'pending':
        case 'pending_payment':
          stats.pending++;
          break;
        case 'processing':
        case 'accepted':
          stats.processing++;
          break;
        case 'shipped':
          stats.shipped++;
          break;
        case 'delivered':
          stats.delivered++;
          break;
        case 'cancelled':
        case 'rejected':
        case 'disputed':
          stats.cancelled++;
          break;
      }
    });

    return stats;
  };

  const stats = getTotalOrders();

  const handleRefresh = () => {
    fetchOrders();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-32 bg-gray-200 rounded-xl"></div>
              ))}
            </div>
            <div className="h-64 bg-gray-200 rounded-xl"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">My Orders</h1>
              <p className="text-gray-600 mt-2">Track and manage all your purchases</p>
            </div>
            
            <div className="mt-4 md:mt-0 flex space-x-3">
              <button
                onClick={handleRefresh}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Refresh
              </button>
              <button
                onClick={() => navigate('/')}
                className="px-6 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg font-semibold hover:from-orange-600 hover:to-red-600"
              >
                Shop More
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="text-2xl font-bold text-gray-900">{stats.all}</div>
            <div className="text-sm text-gray-600">Total Orders</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
            <div className="text-sm text-gray-600">Pending</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="text-2xl font-bold text-blue-600">{stats.processing}</div>
            <div className="text-sm text-gray-600">Processing</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="text-2xl font-bold text-purple-600">{stats.shipped}</div>
            <div className="text-sm text-gray-600">Shipped</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="text-2xl font-bold text-green-600">{stats.delivered}</div>
            <div className="text-sm text-gray-600">Delivered</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="text-2xl font-bold text-red-600">{stats.cancelled}</div>
            <div className="text-sm text-gray-600">Cancelled</div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            {/* Status Filters */}
            <div className="flex flex-wrap gap-2">
              {['all', 'pending', 'processing', 'shipped', 'delivered', 'cancelled'].map((status) => (
                <button
                  key={status}
                  onClick={() => setFilter(status)}
                  className={`px-4 py-2 rounded-lg font-medium capitalize ${
                    filter === status
                      ? 'bg-orange-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {status === 'all' ? 'All Orders' : status}
                  {status !== 'all' && (
                    <span className="ml-2 px-2 py-1 bg-white/30 rounded text-xs">
                      {stats[status]}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search orders..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent w-full md:w-64"
              />
            </div>
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {filteredOrders.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Order ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Product
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Seller
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date & Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Delivery
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredOrders.map((order) => (
                    <tr key={order.order_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          #{order.order_id}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatDate(order.created_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="h-12 w-12 flex-shrink-0">
                            {order.listing_image ? (
                              <img
                                className="h-12 w-12 rounded-lg object-cover"
                                src={`http://192.168.0.175/campus_marketplace/backend${order.listing_image}`}
                                alt={order.listing_title}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHZpZXdCb3g9IjAgMCA0OCA0OCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHJ4PSI4IiBmaWxsPSIjRjJGMkYyIi8+PHBhdGggZD0iTTI0IDE4VjMwTTE4IDI0SDMwIiBzdHJva2U9IiM5Q0EwQTYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+PC9zdmc+';
                                }}
                              />
                            ) : (
                              <div className="h-12 w-12 rounded-lg bg-gray-200 flex items-center justify-center">
                                <Package className="w-6 h-6 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 truncate max-w-xs">
                              {order.listing_title || 'Unnamed Product'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {order.category_name || 'Uncategorized'} • {order.listing_condition || 'Unknown'}
                            </div>
                            <div className="text-xs text-gray-400">
                              Qty: {order.quantity || 1}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          {order.seller_profile_picture ? (
                            <img
                              className="h-8 w-8 rounded-full"
                              src={order.seller_profile_picture}
                              alt={`${order.seller_first_name} ${order.seller_last_name}`}
                            />
                          ) : (
                            <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                              <span className="text-xs font-medium text-gray-600">
                                {order.seller_first_name?.charAt(0)}{order.seller_last_name?.charAt(0)}
                              </span>
                            </div>
                          )}
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {order.seller_first_name} {order.seller_last_name}
                            </div>
                            {order.store_name && (
                              <div className="text-xs text-gray-500">{order.store_name}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-lg font-bold text-gray-900">
                          ₦{parseFloat(order.total_amount || 0).toLocaleString()}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center mt-1">
                          <DollarSign className="w-3 h-3 mr-1" />
                          {order.payment_status === 'paid' ? 'Paid' : 'Pending'}
                        </div>
                        {order.negotiated_price && (
                          <div className="text-xs text-gray-400 line-through">
                            ₦{parseFloat(order.unit_price || 0).toLocaleString()}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getStatusIcon(order)}
                          <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order)}`}>
                            {getStatusText(order)}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {order.payment_status === 'paid' && order.paid_at && (
                            <span>Paid: {formatDate(order.paid_at)}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {order.delivery_option === 'delivery' ? (
                            <div className="flex items-center">
                              <Truck className="w-4 h-4 mr-1 text-gray-400" />
                              Delivery
                            </div>
                          ) : (
                            <div className="flex items-center">
                              <MapPin className="w-4 h-4 mr-1 text-gray-400" />
                              Pickup
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {order.delivery_deadline && (
                            <div className="flex items-center">
                              <Calendar className="w-3 h-3 mr-1" />
                              {formatDate(order.delivery_deadline)}
                            </div>
                          )}
                        </div>
                        {order.delivery_fee && parseFloat(order.delivery_fee) > 0 && (
                          <div className="text-xs text-gray-400">
                            Fee: ₦{parseFloat(order.delivery_fee).toLocaleString()}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex flex-col space-y-2">
                          <button
                            onClick={() => navigate(`/my-orders/${order.order_id}`)}
                            className="text-orange-600 hover:text-orange-900 text-left"
                          >
                            View Details
                          </button>
                          {getDisplayStatus(order) === 'delivered' && (
                            <button className="text-blue-600 hover:text-blue-900 text-left">
                              Rate Seller
                            </button>
                          )}
                          {order.disputed === 1 && (
                            <button className="text-red-600 hover:text-red-900 text-left">
                              View Dispute
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                <Package className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {searchQuery ? 'No orders found' : 'No orders yet'}
              </h3>
              <p className="text-gray-600 max-w-md mx-auto mb-6">
                {searchQuery
                  ? 'No orders match your search. Try different keywords.'
                  : filter !== 'all'
                  ? `You don't have any ${filter} orders.`
                  : "You haven't placed any orders yet. Start shopping to see your orders here."
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {(searchQuery || filter !== 'all') && (
                  <button
                    onClick={() => {
                      setFilter('all');
                      setSearchQuery('');
                    }}
                    className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                  >
                    View All Orders
                  </button>
                )}
                <button
                  onClick={() => navigate('/')}
                  className="px-6 py-2 border-2 border-orange-500 text-orange-500 rounded-lg hover:bg-orange-50"
                >
                  Start Shopping
                </button>
              </div>
            </div>
          )}

          {/* Pagination */}
          {filteredOrders.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing <span className="font-medium">1</span> to{' '}
                <span className="font-medium">{filteredOrders.length}</span> of{' '}
                <span className="font-medium">{filteredOrders.length}</span> orders
              </div>
              <div className="flex space-x-2">
                <button className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50">
                  Previous
                </button>
                <button className="px-3 py-1 border border-gray-300 rounded text-sm bg-orange-50 text-orange-600 border-orange-200">
                  1
                </button>
                {filteredOrders.length > 10 && (
                  <>
                    <button className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50">
                      2
                    </button>
                    <button className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50">
                      Next
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Download Button */}
        {filteredOrders.length > 0 && (
          <div className="mt-6 text-right">
            <button className="inline-flex items-center px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900">
              <Download className="w-4 h-4 mr-2" />
              Export Orders
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyOrders;